'use client'

import { useHydration } from '@/hooks/use-hydration'
import { useIsDesktop } from '@/hooks/use-screen'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { useState } from 'react'
import { MdAdd, MdRemove } from 'react-icons/md'

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/atoms/ui/accordion'
import { Button } from '@/components/atoms/ui/button'
import Container from '@/components/templates/container/container'

import { formatString } from '@/lib/utils/format-string'
import { getEntryData } from '@/lib/utils/get-entry-data'

interface LayananFAQProps {
  language?: 'id' | 'en'
  data: Service
  activeTab?: string
}

export const LayananFAQ = ({ language = 'id', data, activeTab }: LayananFAQProps) => {
  const activeData = activeTab
    ? (data.includes.Entry.find(
        (tab) => formatString(tab.fields.title) === formatString(activeTab)
      ) as Service['includes']['Entry'][0])
    : data.items[0]

  const isDesktop = useIsDesktop()
  const isHydrated = useHydration()
  const [show, setShow] = useState(isDesktop ? activeData?.fields?.faq?.length : 3)

  const handleLoad = () => {
    if (show >= activeData?.fields?.faq?.length) {
      setShow(3)
    } else {
      setShow(show + activeData?.fields?.faq?.length - 3)
    }
  }

  if (isHydrated && activeData?.fields?.faq?.length > 0) {
    return (
      <section className="pt-5 pb-12 lg:pb-20 lg:pt-14">
        <Container className="space-y-6 lg:space-y-10 max-w-[875px]">
          <h2 className="text-2xl font-extrabold text-center lg:text-3xl text-primary-blue">
            {language === 'id' ? 'Pertanyaan Umum' : 'Frequently Asked Questions '}
          </h2>
          <Accordion type="single" collapsible className="space-y-3">
            {activeData.fields.faq.slice(0, show).map((item, index) => {
              const faq = getEntryData(item.sys.id, data)

              return (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="border border-gray-300 rounded-xl flex justify-between !items-center"
                >
                  <AccordionTrigger
                    customArrow={<span />}
                    className="[&[data-state=open]>div>span>#add]:hidden [&[data-state=open]>div>span>#remove]:!block px-4 py-5 lg:p-6"
                  >
                    <div className="w-[84%] pr-4 sm:w-[93%] border-r border-gray-300 text-start">
                      <div className="w-full font-bold sm:text-lg lg:text-xl text-primary-blue">{faq?.fields.title}</div>
                      <AccordionContent className="pt-4 text-sm lg:text-base container-markdown">
                        {faq?.fields?.content && documentToReactComponents(faq.fields.content)}
                      </AccordionContent>
                    </div>
                    <div className="flex justify-end w-[16%] sm:w-[7%] pl-4">
                      <span className="flex items-center justify-center w-8 h-8 p-1 text-gray-300 border border-gray-400 rounded-full">
                        <MdAdd id="add" />
                        <MdRemove id="remove" className="hidden" />
                      </span>
                    </div>
                  </AccordionTrigger>
                </AccordionItem>
              )
            })}
          </Accordion>

          {activeData?.fields?.faq?.length > 3 && (
            <div className="flex justify-center lg:hidden">
              <Button
                onClick={handleLoad}
                variant="outline"
                className="mx-auto font-semibold border-primary-blue text-primary-blue hover:bg-primary-blue hover:text-white"
              >
                {show >= activeData?.fields?.faq?.length ? 'Show Less' : 'Show More'}
              </Button>
            </div>
          )}
        </Container>
      </section>
    )
  }

  return null
}
